/**
 * The text of a PriceTag (components/ds/PriceTag.tsx), kept free of React so it can be tested on its own
 * (priceTagFormat.test.ts).
 *
 * The price per mg comes from formatPricePerMg in src/lib/listingPicks.ts, the helper every price block on the
 * site already uses ("$4.25/mg", "$0.117/mg" under a dollar, "$2.50/g" under a cent per mg), so a PriceTag can
 * never print a listing's price per mg differently from the compound page or /compare.
 */
import { formatPricePerMg } from "@/lib/listingPicks";

export interface PriceTagInput {
  /** The listing's price in US dollars. */
  price: number | null | undefined;
  /** How many mg the listing holds. With it the headline is the price per mg. */
  totalMg?: number | null;
  /** The size as the store words it: "10 mg vial", "60 capsules". */
  sizeLabel?: string | null;
  /** When the price was last checked: an ISO date or timestamp, or a Date. */
  checkedAt?: string | Date | null;
}

export interface PriceTagParts {
  /** "$12.00/mg", "$2.50/g", "$49.99" (a pack price when there is no mg count) or "No price on file". */
  headline: string;
  /** What the headline is, for the screen-reader label and for styling the empty state. */
  kind: "per-mg" | "pack" | "none";
  /** The second line: "10 mg vial, $119.99", or the size alone under a pack price. Null when there is nothing. */
  detail: string | null;
  /** "Checked Oct 21, 2026", or null without a valid date. */
  checked: string | null;
  /** One sentence for screen readers: "$12.00 per mg. 10 mg vial, $119.99. Checked Oct 21, 2026." */
  label: string;
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$119.99", "$1,250.00". */
export function formatUsd(value: number): string {
  return usd.format(value);
}

const mgNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** "10 mg", "0.5 mg", "30 g" for 30,000 mg. */
export function formatAmountMg(totalMg: number): string {
  if (totalMg >= 1000 && totalMg % 1000 === 0) return `${mgNumber.format(totalMg / 1000)} g`;
  return `${mgNumber.format(totalMg)} mg`;
}

const checkedDate = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/** "Checked Oct 21, 2026". Dates are read in UTC, so a date-only string ("2026-10-21") shows that same day everywhere. */
export function formatChecked(checkedAt: string | Date | null | undefined): string | null {
  if (!checkedAt) return null;
  const d = checkedAt instanceof Date ? checkedAt : new Date(checkedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `Checked ${checkedDate.format(d)}`;
}

export function priceTagParts({ price, totalMg, sizeLabel, checkedAt }: PriceTagInput): PriceTagParts {
  const hasPrice = typeof price === "number" && Number.isFinite(price) && price > 0;
  const hasMg = typeof totalMg === "number" && Number.isFinite(totalMg) && totalMg > 0;
  const size = sizeLabel?.trim() || (hasMg ? formatAmountMg(totalMg as number) : "");
  const checked = formatChecked(checkedAt);

  let headline: string;
  let kind: PriceTagParts["kind"];
  let detail: string | null;
  if (!hasPrice) {
    headline = "No price on file";
    kind = "none";
    detail = size || null;
  } else if (hasMg) {
    headline = formatPricePerMg(price as number, totalMg as number);
    kind = "per-mg";
    detail = size ? `${size}, ${formatUsd(price as number)}` : formatUsd(price as number);
  } else {
    headline = formatUsd(price as number);
    kind = "pack";
    detail = size || null;
  }

  // "$12.00/mg" reads as "12 dollars slash mg"; the label spells the unit out.
  const spoken = headline.replace(/\/mg$/, " per mg").replace(/\/g$/, " per gram");
  const label = [spoken, detail, checked].filter(Boolean).join(". ") + ".";
  return { headline, kind, detail, checked, label };
}
